import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  EmailSearchResult,
  EmailSearchResultContact,
  EmailSearchResultProject,
} from '../../../models/Message';
import { createTemporaryId, distinctByProperty } from '../../../util';
import { apiSearchEmailAddress } from '../api';
import useProjectsContext from '../../projects/hooks/useProjectsContext';
import { AddressesContext } from '../context/AddressesContext';
import { useProjectContacts } from '../../projects/hooks/useProjectContacts';
import { ProjectId, ProjectRole } from '../../../models/Types';

export interface EmailSearchResultWithProjectContact
  extends EmailSearchResultContact {
  projectRoles?: ProjectRole[];
  jobTitle?: string;
  isInternal?: boolean;
}

interface UseAddressSearch {
  addresses: EmailSearchResult[];
  suggestions: EmailSearchResult[];
  internalProjectContacts: EmailSearchResultWithProjectContact[];
  externalProjectContacts: EmailSearchResultWithProjectContact[];
  additionalContacts: EmailSearchResult[];
  isSearching: boolean;
  isLoadingAdditionalContacts: boolean;
  refreshAddressSuggestions: () => void;
}

const useAddressSearch = (
  searchTerm: string,
  projectId: ProjectId,
  selectedAddresses?: string[]
): UseAddressSearch => {
  const { getProjectById } = useProjectsContext();

  const {
    addressSuggestions,
    isLoading: isLoadingAddressSuggestions,
    refetchAddressSugestions: refreshAddressSuggestions,
    getAddressByMail,
  } = useContext(AddressesContext);

  const [addressesFromOnlineSearch, setAddressesFromOnlineSearch] = useState<
    EmailSearchResult[]
  >([]);

  const [isLoadingAdditionalContacts, setIsLoadingAdditionalContacts] =
    useState<boolean>(false);

  // Get project contacts
  const {
    internalProjectContactContacts,
    externalProjectContactContacts,
    internalProjectContacts: internalProjectContactsData,
    externalProjectContacts: externalProjectContactsData,
  } = useProjectContacts(projectId);

  // Convert project contacts to EmailSearchResult format
  const internalProjectContactResults: EmailSearchResultWithProjectContact[] =
    useMemo(() => {
      return internalProjectContactsData
        .map((contact) => {
          const contactData = internalProjectContactContacts.find(
            (c) => c?.contactId === contact.contactId
          );
          if (!contactData?.eMail) return null;
          return {
            id: contact.internalProjectContactId,
            eMail: contactData.eMail,
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            type: 'contact' as const,
            isSuggestion: false,
            projectRoles: contact.projectRoles,
            jobTitle: contact.jobTitle,
            isInternal: true,
          } as EmailSearchResultWithProjectContact;
        })
        .filter(Boolean);
    }, [internalProjectContactsData, internalProjectContactContacts]);

  const externalProjectContactResults: EmailSearchResultWithProjectContact[] =
    useMemo(() => {
      return externalProjectContactsData
        .map((contact) => {
          const contactData = externalProjectContactContacts.find(
            (c) => c?.contactId === contact.contactId
          );
          if (!contactData?.eMail) return null;
          return {
            id: contact.externalProjectContactId,
            eMail: contactData.eMail,
            firstName: contactData.firstName,
            lastName: contactData.lastName,
            type: 'contact' as const,
            isSuggestion: false,
            projectRoles: [],
            jobTitle: contact.jobTitle,
            isInternal: false,
          } as EmailSearchResultWithProjectContact;
        })
        .filter(Boolean);
    }, [externalProjectContactsData, externalProjectContactContacts]);

  const onlineSearchResults = useMemo(
    () => distinctByProperty(addressesFromOnlineSearch, 'eMail'),
    [addressesFromOnlineSearch]
  );

  const addressSuggestionsWithSelectedValues = useMemo(() => {
    return distinctByProperty(
      addressSuggestions.concat(
        (selectedAddresses ?? []).map(
          (address) =>
            getAddressByMail(address) ??
            ({
              id: createTemporaryId(),
              eMail: address,
              type: 'external',
              isSuggestion: false,
            } as EmailSearchResult)
        )
      ),
      'eMail'
    );
  }, [addressSuggestions, selectedAddresses, getAddressByMail]);

  // Filter function for local search
  const filterBySearchTerm = useCallback(
    (contact: EmailSearchResult | EmailSearchResultWithProjectContact) => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const contactWithNames = contact as EmailSearchResultContact;
      return (
        contact.eMail?.toLowerCase().includes(term) ||
        contactWithNames.firstName?.toLowerCase().includes(term) ||
        contactWithNames.lastName?.toLowerCase().includes(term)
      );
    },
    [searchTerm]
  );

  useEffect(() => {
    if (searchTerm && searchTerm.length > 2) {
      // Start backend search after 350ms
      const timer = setTimeout(async () => {
        setIsLoadingAdditionalContacts(true);
        try {
          const { result, data } = await apiSearchEmailAddress(searchTerm);
          if (result.status >= 200 && result.status < 300) {
            const filterFn = (
              item: EmailSearchResultContact | EmailSearchResultProject
            ) => {
              const project = getProjectById(item?.id);
              const parentProject = getProjectById(project?.parentProject);
              return (
                item?.eMail &&
                (item?.type !== 'project' ||
                  !project?.parentProject ||
                  (parentProject &&
                    parentProject?.eMailPrefix !== project?.eMailPrefix))
              );
            };
            // no sorting because be response is sorted
            setAddressesFromOnlineSearch(data.filter(filterFn));
          }
        } catch {
          console.warn('Error occured in useAddressSearch');
        }
        setIsLoadingAdditionalContacts(false);
      }, 350);

      return () => {
        clearTimeout(timer);
      };
    } else {
      setAddressesFromOnlineSearch([]);
      setIsLoadingAdditionalContacts(false);
      return undefined;
    }
  }, [searchTerm, getProjectById]);

  return useMemo(() => {
    // Filter project contacts based on search term
    const filteredSuggestions = searchTerm
      ? addressSuggestionsWithSelectedValues.filter(filterBySearchTerm)
      : addressSuggestionsWithSelectedValues;

    // Deduplicate each subsequent group against already-shown emails
    const suggestionsEmailSet = new Set(
      filteredSuggestions.map((a) => a.eMail)
    );

    const filteredInternalContacts = (
      searchTerm
        ? internalProjectContactResults.filter(filterBySearchTerm)
        : internalProjectContactResults
    ).filter((c) => !suggestionsEmailSet.has(c.eMail));

    const internalEmailSet = new Set([
      ...Array.from(suggestionsEmailSet),
      ...filteredInternalContacts.map((a) => a.eMail),
    ]);

    const filteredExternalContacts = (
      searchTerm
        ? externalProjectContactResults.filter(filterBySearchTerm)
        : externalProjectContactResults
    ).filter((c) => !internalEmailSet.has(c.eMail));

    const displayedEmails = new Set([
      ...Array.from(internalEmailSet),
      ...filteredExternalContacts.map((a) => a.eMail),
    ]);

    // Filter additional contacts to exclude already displayed ones
    const filteredAdditionalContacts = onlineSearchResults.filter(
      (contact) => !displayedEmails.has(contact.eMail)
    );

    // Combine all addresses for backward compatibility
    const allAddresses = [
      ...filteredSuggestions,
      ...filteredInternalContacts,
      ...filteredExternalContacts,
      ...filteredAdditionalContacts,
    ];

    return {
      addresses: allAddresses,
      suggestions: filteredSuggestions,
      internalProjectContacts: filteredInternalContacts,
      externalProjectContacts: filteredExternalContacts,
      additionalContacts: filteredAdditionalContacts,
      isSearching: isLoadingAddressSuggestions,
      isLoadingAdditionalContacts,
      refreshAddressSuggestions,
    };
  }, [
    searchTerm,
    onlineSearchResults,
    addressSuggestionsWithSelectedValues,
    internalProjectContactResults,
    externalProjectContactResults,
    isLoadingAdditionalContacts,
    isLoadingAddressSuggestions,
    refreshAddressSuggestions,
    filterBySearchTerm,
  ]);
};

export default useAddressSearch;
